import type { ThemeMeta, ThemeTokens } from '@/previewThemes/meta';

/**
 * LittleLove's design constants.
 *
 * Named LittleLove in the gallery, `voltic` on disk. The id is the folder name
 * and a merchant's stored theme is keyed on it - `preset`, `layout.family` and
 * every `voltic.*` content override - so it stays put; the label is the only
 * part a merchant reads. Decor and PowerTools do the same thing, which is also
 * why the prose in the other themes still calls this one Voltic: that is its
 * id, and an id is what one file means when it points at another.
 *
 * A dense marketplace: a utility rail, a masthead, a category bar under it,
 * a filter rail down the side of the catalogue and four columns of product
 * beside it. The shop it is previewed with is a baby and kids marketplace -
 * see `catalog.ts` for why it ships its own, and `photos.ts` for the range it
 * is photographed with.
 */
export const tokens: ThemeTokens = {
  // One face doing both jobs. Archivo is a grotesque with a low-contrast,
  // slightly condensed cut, which is what lets a heading sit at -0.025em
  // without the letters closing up on each other.
  headingFont: 'Archivo',
  // The signal colour. Everything structural here is ink on white; this is
  // the one saturated note, and it is what a price drop, a sale badge and
  // the wordmark's full stop are painted with.
  accent: '#e14b39',
  // A marketplace is read as much in numbers as in words - prices, stock
  // counts, discount codes, delivery windows, the size on every card - and
  // this theme sets all of them in a monospace so they line up column to
  // column.
  monoFont: 'JetBrains Mono',
  headingTracking: '-0.025em',
  headingTransform: 'none',
  // 1440px. The widest of the themes by some way: this one is drawn around
  // a filter rail plus four columns of catalogue, and anything narrower
  // takes a column off the grid rather than tightening it.
  containerMax: '90rem',
  // Below 1 on purpose, and the only theme that goes there. Density is the
  // point - a parent comparing eleven sleepsuits wants more of them on screen,
  // not more air between them.
  spaceScale: 0.9,
  // The utility rail, the masthead and the category row under it.
  headerHeight: '9.75rem',
};

export const meta: ThemeMeta = {
  label: 'LittleLove',
  hint: 'A dense baby and kids marketplace: utility rail, hairline grid and monospaced prices.',
  // Not "Fashion & Apparel", although two thirds of the catalogue is clothing.
  // The chips are how a merchant narrows the gallery to the two or three themes
  // worth opening, and a baby marketplace is not one of the answers to "I sell
  // clothes" - it is the answer to "I sell to parents", which is a different
  // shop with a different filter rail on it.
  industries: ['toys_baby', 'general'],
  look: {
    // Ink on white with a hairline grid; the vermilion this theme signs
    // with is an `accent` in `tokens`, not a merchant-editable primary.
    colors: {
      primary: '#14161a',
      background: '#ffffff',
      surface: '#f4f5f7',
      text: '#14161a',
      muted: '#6a7180',
    },
    typography: { font_family: 'Archivo', heading_weight: '600' },
    buttons: { radius_px: 6, style: 'solid' },
    // No `catalog` named: this theme ships its own in `catalog.ts`. A baby and
    // kids marketplace is part of its identity - eight departments, twenty-five
    // shelves, four sizes on almost everything - and none of the six industry
    // shops has that shape to fill a filter rail with.
  },
};
